/** @jsx jsx */
import React, { type MouseEvent, type ReactNode } from 'react';
import { jsx, css } from '@compiled/react';
import { Grid } from '@atlaskit/primitives';
import { SimpleTag as Tag } from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import type { Color, ColorSchema } from '@atlassian/jira-issue-epic-color/src/common/types.tsx';
import { transformColorToTheme } from '@atlassian/jira-issue-epic-color/src/common/utils.tsx';
import { PARENT_SWITCHER_LOZENGE_TEST_ID } from '../../constants';

const colorIndicatorStyles = css({
	flexShrink: 0,
	width: token('space.200'),
	height: token('space.200'),
	borderRadius: '3px',
});

export const EpicColorIndicator = ({ color }: { color: string }) => {
	return (
		// eslint-disable-next-line jira/react/no-style-attribute
		<div css={colorIndicatorStyles} style={{ backgroundColor: color }} />
	);
};

export type EpicTagProps = {
	color?: Color;
	summary: string;
	// These props are required by dropdown trigger.
	['aria-controls']?: string;
	['aria-expanded']?: boolean;
	['aria-haspopup']?: boolean;
	testId?: string;
	onClick?: (e: MouseEvent<HTMLElement>) => void;
	maxWidth?: number | string;
	isCompactView?: boolean;
};

export const EpicTag = ({ color, summary, isCompactView }: EpicTagProps) => {
	const paletteColorValues: Record<Color, string> = transformColorToTheme(
		(colorSchema: ColorSchema) => colorSchema.primary,
	);
	const selectedColor = (color && paletteColorValues[color]) || paletteColorValues.PURPLE;

	return isCompactView ? (
		<ToolTip content={summary}>
			{selectedColor && (
				<EpicColorIndicator
					data-component-selector="parent-switcher-epic-lozenge-wrapper"
					color={selectedColor}
				/>
			)}
		</ToolTip>
	) : (
		<TagHack
			data-component-selector="parent-switcher-epic-lozenge-wrapper"
			data-testid={PARENT_SWITCHER_LOZENGE_TEST_ID}
			tagColor={selectedColor}
		>
			<ToolTip content={summary} hideTooltipOnClick>
				<Grid>
					<Tag testId="issue-field-parent-switcher.common.ui.epic-tag.tag" text={summary} />
				</Grid>
			</ToolTip>
		</TagHack>
	);
};

//! This is used to override the default theme as Tag doesn't support custom styles.
const tagHackStyles = css({
	display: 'inline-block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > div > div > span': {
		borderColor: 'var(--epic-tag-border-color)',
	},
});

const TagHack = ({ tagColor, children }: { tagColor: string; children: ReactNode }) => (
	<div
		css={tagHackStyles}
		// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop, @typescript-eslint/consistent-type-assertions
		style={{ '--epic-tag-border-color': tagColor } as React.CSSProperties}
	>
		{children}
	</div>
);
